import { useMemo } from "react";
import { useAuth } from "./useAuth";
import { useMyBusiness } from "./useBusiness";
import type { SectionKey, UserSectionPermissions } from "../api/types";

export function usePermissions() {
  const { user, isAuthenticated } = useAuth();
  const { data: business, isLoading: businessLoading, isError: businessError } = useMyBusiness();

  const sectionPermissions: UserSectionPermissions = useMemo(() => {
    if (!business?.section_permissions) return {};
    return Object.fromEntries(
      Object.entries(business.section_permissions).map(([section, p]) => [
        section,
        { canView: p.can_view, canEdit: p.can_edit },
      ])
    ) as UserSectionPermissions;
  }, [business]);

  const permissionsLoaded = Object.keys(sectionPermissions).length > 0;
  // A conclusive "no business" state: the query resolved (not loading) and
  // returned nothing. Used to hide business-scoped nav for users who operate
  // above the tenant layer (platform admins).
  const noBusiness = !businessLoading && (businessError || !business);

  function canView(section: SectionKey): boolean {
    if (noBusiness) return false;
    // While permissions are loading (or absent), keep everything visible
    if (!permissionsLoaded) return true;
    return sectionPermissions[section]?.canView ?? false;
  }

  function canEdit(section: SectionKey): boolean {
    if (noBusiness) return false;
    if (!permissionsLoaded) return true;
    return sectionPermissions[section]?.canEdit ?? false;
  }

  return {
    isAuthenticated,
    isPlatformAdmin: user?.is_platform_admin ?? false,
    isBusinessAdmin: business?.is_viewer_admin ?? false,
    sectionPermissions,
    canView,
    canEdit,
    user,
  };
}
